import React, { useEffect, useState, useCallback, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaQrcode, FaDownload } from 'react-icons/fa';
import apiClient from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useLanguage } from '../utils/i18n';

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('OrderDetail Error:', error, errorInfo);
  }

  render() {
    const t = this.props.t || ((key) => key);
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold mb-4">{t('errorOccurred')}</h1>
            <p className="text-gray-600 mb-4">{t('failedLoadOrderPage')}</p>
            <Link
              to="/orders"
              className="inline-block bg-black text-white px-6 py-3 rounded hover:bg-gray-900"
            >
              {t('backToOrderList')}
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function OrderDetailContent() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const { t, formatCurrency } = useLanguage();

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) return;
      
      try {
        setLoading(true);
        setError('');
        const response = await apiClient.get(`/orders/${orderId}`);
        if (response.data.success && response.data.data) {
          setOrder(response.data.data);
        } else {
          setError(t('invalidOrderData'));
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        if (err.response?.status === 404) {
          setError(t('orderNotFound'));
        } else if (err.response?.status === 401) {
          setError(t('pleaseLoginToViewOrder'));
        } else {
          setError(err.response?.data?.message || t('failedLoadOrder'));
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrder();
  }, [orderId]);

  const fetchQRCode = useCallback(async () => {
    if (!order?.unique_token) return;
    try {
      setQrLoading(true);
      const response = await apiClient.get(`/orders/track/${order.unique_token}/qrcode-data`);
      if (response.data.success) {
        setQrData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching QR code:', err);
    } finally {
      setQrLoading(false);
    }
  }, [order?.unique_token]);

  useEffect(() => {
    if (order) {
      fetchQRCode();
    }
  }, [order, fetchQRCode]);

  const handleDownloadQR = () => {
    if (!qrData?.qr_code) return;
    const link = document.createElement('a');
    link.href = qrData.qr_code;
    link.download = `invoice-qr-${qrData.order_number || orderId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: t('waitingConfirmation') },
      confirmed: { color: 'bg-blue-100 text-blue-800', label: t('confirmed') },
      processing: { color: 'bg-purple-100 text-purple-800', label: t('processing') },
      shipped: { color: 'bg-indigo-100 text-indigo-800', label: t('shipped') },
      delivered: { color: 'bg-green-100 text-green-800', label: t('completed') },
      cancelled: { color: 'bg-red-100 text-red-800', label: t('cancelled') }
    };
    return statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
  };

  const getPaymentBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: t('unpaid') },
      paid: { color: 'bg-green-100 text-green-800', label: t('paid') },
      failed: { color: 'bg-red-100 text-red-800', label: t('paymentFailed') },
      refunded: { color: 'bg-gray-100 text-gray-800', label: t('refund') }
    };
    return statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
  };

  const getStatusStep = (status) => {
    const steps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
    return steps.indexOf(status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">{t('orderNotFound')}</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link
            to="/orders"
            className="inline-block bg-black text-white px-6 py-3 rounded hover:bg-gray-900"
          >
            {t('backToOrderList')}
          </Link>
        </div>
      </div>
    );
  }

  const statusBadge = getStatusBadge(order.status);
  const paymentBadge = getPaymentBadge(order.payment_status);
  const currentStep = getStatusStep(order.status);

  return (
    <>
      <Helmet>
        <title>{`Order ${order.order_number || '#' + order.id} - Marketplace Jeans`}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="mb-6 flex flex-wrap justify-between items-center gap-4">
            <div>
              <Link to="/orders" className="text-blue-600 hover:underline mb-2 inline-block">
                ← {t('backToOrderList')}
              </Link>
              <h1 className="text-3xl font-bold">{t('orderNumber')} {order.order_number || `#${order.id}`}</h1>
              <p className="text-gray-600">
                {new Date(order.created_at).toLocaleDateString('id-ID', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div className="flex gap-2">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${statusBadge.color}`}>
                {statusBadge.label}
              </span>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${paymentBadge.color}`}>
                {paymentBadge.label}
              </span>
            </div>
          </div>

          {/* Order Progress */}
          {order.status !== 'cancelled' && (
            <div className="bg-white rounded shadow p-6 mb-6">
              <h2 className="font-bold mb-4">{t('orderStatus')}</h2>
              <div className="flex justify-between items-center relative">
                {/* Progress Line */}
                <div className="absolute top-4 left-0 right-0 h-1 bg-gray-200">
                  <div
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${(currentStep / 4) * 100}%` }}
                  />
                </div>

                {/* Steps */}
                {[t('pending'), t('confirmed'), t('processing'), t('shipped'), t('delivered')].map((label, idx) => (
                  <div key={idx} className="relative z-10 flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                        ${idx <= currentStep ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                    >
                      {idx < currentStep ? '✓' : idx + 1}
                    </div>
                    <span className="text-xs mt-2 text-center">{label}</span>
                  </div>
                ))}
              </div>

              {order.tracking_number && (
                <div className="mt-6 p-4 bg-blue-50 rounded">
                  <p className="font-semibold">{t('trackingNumber')}: {order.tracking_number}</p>
                  <p className="text-sm text-gray-600">
                    {t('shippingLabel')}: {order.shipping_method}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Order Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded shadow p-6">
                <h2 className="font-bold mb-4">{t('orderedProducts')}</h2>
                <div className="space-y-4">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex gap-4 pb-4 border-b last:border-0">
                      <img
                        src={getImageUrl(item.product_image, 'product')}
                        alt={item.product_name || 'Product'}
                        className="w-20 h-20 object-cover rounded bg-gray-100"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                        }}
                      />
                      <div className="flex-1">
                        <Link
                          to={`/products/${item.product_slug}`}
                          className="font-semibold hover:text-blue-600"
                        >
                          {item.product_name}
                        </Link>
                        {item.size_name && (
                          <p className="text-sm text-gray-600">{t('size')}: {item.size_name}</p>
                        )}
                        <p className="text-sm text-gray-600">
                          {item.quantity}x @ {formatCurrency(item.price)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">
                          {formatCurrency(item.total)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1 space-y-6">
              {/* Payment Summary */}
              <div className="bg-white rounded shadow p-6">
                <h2 className="font-bold mb-4">{t('paymentSummary')}</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('subtotal')}</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('shippingCost')}</span>
                    <span>{formatCurrency(order.shipping_cost)}</span>
                  </div>
                  {parseFloat(order.discount_amount || 0) > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{t('discount')}</span>
                      <span>-{formatCurrency(order.discount_amount)}</span>
                    </div>
                  )}
                  {parseFloat(order.tax || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>{t('ppn')}</span>
                      <span>{formatCurrency(order.tax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span>{t('total')}</span>
                    <span className="text-red-600">
                      {formatCurrency(order.total)}
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-600">{t('paymentMethod')}</p>
                  <p className="font-semibold">{order.payment_method || t('bankTransfer')}</p>
                </div>
              </div>

              {/* Shipping Info */}
              <div className="bg-white rounded shadow p-6">
                <h2 className="font-bold mb-4">{t('shippingAddress')}</h2>
                <div className="text-sm">
                  <p className="font-semibold">{order.customer_name || '-'}</p>
                  <p>{order.customer_phone || '-'}</p>
                  <p className="mt-2">{order.shipping_address || '-'}</p>
                  <p>
                    {order.shipping_city || ''}
                    {order.shipping_province && `, ${order.shipping_province}`}
                    {order.shipping_postal_code && ` ${order.shipping_postal_code}`}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {order.notes && (
                <div className="bg-white rounded shadow p-6">
                  <h2 className="font-bold mb-4">{t('notes')}</h2>
                  <p className="text-sm text-gray-600">{order.notes}</p>
                </div>
              )}

              {/* QR Code Invoice */}
              <div className="bg-white rounded shadow p-6">
                <h2 className="font-bold mb-4 flex items-center gap-2">
                  <FaQrcode className="text-gray-700" />
                  QR Code Invoice
                </h2>
                {qrLoading ? (
                  <div className="flex justify-center py-6">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
                  </div>
                ) : qrData ? (
                  <div className="flex flex-col items-center">
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-3 mb-3">
                      <img
                        src={qrData.qr_code}
                        alt={`QR Code - ${qrData.order_number}`}
                        className="w-48 h-48"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mb-1 text-center">
                      {t('orderNumber')}: {qrData.order_number}
                    </p>
                    <p className="text-xs text-gray-400 mb-3 text-center">
                      Scan untuk melihat detail & status invoice
                    </p>
                    <button
                      onClick={handleDownloadQR}
                      className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded-lg hover:bg-gray-800 transition"
                    >
                      <FaDownload className="text-xs" />
                      Download QR
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    QR Code tidak tersedia
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-center gap-4">
            {order.tracking_number && (
              <Link
                to={`/orders/${order.id}/tracking`}
                className="px-6 py-3 border-2 border-black text-black rounded hover:bg-gray-100 font-semibold"
              >
                {t('trackShipment')}
              </Link>
            )}
            <Link
              to="/products"
              className="px-6 py-3 bg-black text-white rounded hover:bg-gray-900 font-semibold"
            >
              {t('startShopping')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

// Wrap the component with ErrorBoundary
export default function OrderDetail() {
  const { t } = useLanguage();
  return (
    <ErrorBoundary t={t}>
      <OrderDetailContent />
    </ErrorBoundary>
  );
}
